import { useState } from "react";
import api, { fmtErr } from "@/lib/api";

const SENTIMENTS = ["confident", "patient", "rushed", "fomo", "revenge", "neutral"];

export default function AnalysisModal({ trade, analysis, loading, onClose }) {
  const [showJournal, setShowJournal] = useState(false);
  const [journal, setJournal] = useState({
    title: trade ? `${trade.type} ${trade.symbol || ""} · ${trade.exit_reason || "manual"}` : "",
    outcome: "right",
    sentiment: "neutral",
    notes: "",
    tags: "",
  });
  const [savedId, setSavedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [jErr, setJErr] = useState("");

  if (!trade) return null;
  const pnl = trade.pnl;
  const win = pnl >= 0;
  const grade = analysis?.grade || (loading ? "..." : "—");
  const r = trade.entry_price && trade.stop_loss && trade.take_profit
    ? Math.abs((trade.take_profit - trade.entry_price) / (trade.entry_price - trade.stop_loss)).toFixed(2)
    : "—";

  async function saveJournal() {
    setSaving(true); setJErr("");
    try {
      const auto_tags = [
        trade.type, trade.timeframe, journal.outcome,
        ...(journal.tags ? journal.tags.split(",").map(s => s.trim()).filter(Boolean) : []),
      ];
      const meta_lines = [
        `Direction: ${trade.type}`,
        `Entry: ${trade.entry_price?.toFixed?.(2)}`,
        `SL: ${trade.stop_loss?.toFixed?.(2)}  ·  TP: ${trade.take_profit?.toFixed?.(2)}`,
        `R:R: ${r}  ·  PnL: $${pnl.toFixed(2)}`,
        `Exit: ${trade.exit_reason || "manual"}`,
        analysis?.key_concept_used ? `ICT Concept: ${analysis.key_concept_used}` : null,
        analysis?.grade ? `AI Grade: ${analysis.grade}` : null,
      ].filter(Boolean).join("\n");
      const full_notes = `${meta_lines}\n\n--- Trader notes ---\n${journal.notes}`;
      const { data } = await api.post("/journal/entries", {
        trade_id: trade.id,
        title: journal.title,
        notes: full_notes,
        tags: auto_tags,
        setup: analysis?.key_concept_used || "",
        sentiment: journal.sentiment,
      });
      setSavedId(data.id);
    } catch (e) { setJErr(fmtErr(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  }

  return (
    <div data-testid="analysis-modal" className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4">
      <div className="bg-[#151a24] border border-slate-800 rounded-2xl max-w-lg w-full p-6 shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-4 border-b border-slate-800 pb-3">
          <div>
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <i className="fa-solid fa-brain text-purple-400"/> Institutional Trade Review
              <span className="ml-2 px-2 py-0.5 text-[10px] rounded bg-purple-500/10 text-purple-300 border border-purple-500/30">Claude Sonnet 4.5</span>
            </h2>
            <p className="text-xs text-slate-400">Algorithmic ICT evaluation • Grade: <span className="text-purple-300 font-bold font-mono">{grade}</span></p>
          </div>
          <span data-testid="analysis-outcome" className={`px-2.5 py-1 text-xs font-mono font-bold rounded-md ${win ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/20" : "bg-rose-500/20 text-rose-400 border border-rose-500/20"}`}>
            {win ? `PROFIT: +$${pnl.toFixed(2)}` : `LOSS: -$${Math.abs(pnl).toFixed(2)}`}
          </span>
        </div>
        <div className="space-y-3 my-3">
          <Section icon="fa-circle-check" color="emerald" title="Execution Alignment"
                   text={loading ? "Analyzing..." : analysis?.execution_alignment} testid="analysis-right"/>
          <Section icon="fa-circle-xmark" color="rose" title="Strategic Vulnerabilities"
                   text={loading ? "Analyzing..." : analysis?.vulnerabilities} testid="analysis-wrong"/>
          <Section icon="fa-heart-pulse" color="purple" title="Psychological Evaluation"
                   text={loading ? "Analyzing..." : analysis?.psychology} testid="analysis-psy"/>
          {analysis?.key_concept_used && (
            <div className="text-xs text-slate-400 bg-slate-900/60 border border-slate-800/60 rounded-xl p-2.5">
              Key ICT Concept Detected: <span className="text-amber-400 font-bold">{analysis.key_concept_used}</span>
            </div>
          )}
        </div>

        {/* Auto-Journal section */}
        <div className="mt-3 bg-indigo-500/5 border border-indigo-500/30 rounded-xl p-3" data-testid="auto-journal-block">
          {!showJournal && !savedId && (
            <button data-testid="auto-journal-open" onClick={() => setShowJournal(true)}
              className="w-full py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-bold text-xs">
              <i className="fa-solid fa-book mr-1.5"/> Auto-Journal this Trade
            </button>
          )}
          {showJournal && !savedId && (
            <div className="space-y-2.5">
              <div className="text-[10px] uppercase tracking-widest text-indigo-300 font-bold mb-1">Auto-populated · refine to taste</div>
              <pre className="text-[10px] text-slate-400 bg-slate-900/60 border border-slate-800 rounded p-2 whitespace-pre-wrap font-mono">{`Direction: ${trade.type}\nEntry ${trade.entry_price?.toFixed?.(2)} · SL ${trade.stop_loss?.toFixed?.(2)} · TP ${trade.take_profit?.toFixed?.(2)} · R:R ${r}`}</pre>
              <input data-testid="aj-title" required placeholder="Title" value={journal.title}
                onChange={e => setJournal({ ...journal, title: e.target.value })}
                className="w-full px-2.5 py-1.5 bg-[#161d28] border border-slate-700 rounded text-xs"/>
              <div className="flex gap-2">
                <select data-testid="aj-outcome" value={journal.outcome} onChange={e => setJournal({ ...journal, outcome: e.target.value })}
                  className="flex-1 px-2.5 py-1.5 bg-[#161d28] border border-slate-700 rounded text-xs cursor-pointer">
                  <option value="right">Outcome: Right ✓</option>
                  <option value="wrong">Outcome: Wrong ✗</option>
                </select>
                <select data-testid="aj-sentiment" value={journal.sentiment} onChange={e => setJournal({ ...journal, sentiment: e.target.value })}
                  className="flex-1 px-2.5 py-1.5 bg-[#161d28] border border-slate-700 rounded text-xs cursor-pointer">
                  {SENTIMENTS.map(s => <option key={s} value={s}>Mood: {s}</option>)}
                </select>
              </div>
              <textarea data-testid="aj-notes" rows={2} placeholder="What was the thesis? What went right or wrong?" value={journal.notes}
                onChange={e => setJournal({ ...journal, notes: e.target.value })}
                className="w-full px-2.5 py-1.5 bg-[#161d28] border border-slate-700 rounded text-xs resize-none"/>
              <input data-testid="aj-tags" placeholder="Extra tags (comma-separated)" value={journal.tags}
                onChange={e => setJournal({ ...journal, tags: e.target.value })}
                className="w-full px-2.5 py-1.5 bg-[#161d28] border border-slate-700 rounded text-xs"/>
              {jErr && <div className="text-[10px] text-rose-300">{jErr}</div>}
              <div className="flex gap-2">
                <button data-testid="aj-save" disabled={saving || !journal.title} onClick={saveJournal}
                  className="flex-1 py-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 rounded text-xs font-bold text-white">
                  {saving ? <><i className="fa-solid fa-spinner fa-spin mr-1"/> Saving…</> : "Save Entry"}
                </button>
                <button data-testid="aj-cancel" onClick={() => setShowJournal(false)}
                  className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 rounded text-xs font-bold text-slate-300">Cancel</button>
              </div>
            </div>
          )}
          {savedId && (
            <div data-testid="aj-saved" className="text-center text-emerald-300 text-xs font-bold">
              <i className="fa-solid fa-circle-check mr-1.5"/> Journal entry saved.
            </div>
          )}
        </div>

        <button data-testid="analysis-close" onClick={onClose}
          className="w-full mt-3 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium text-sm transition-all shadow-lg shadow-blue-600/10">
          Acknowledge & Continue Data Stream
        </button>
      </div>
    </div>
  );
}

function Section({ icon, color, title, text, testid }) {
  const colorMap = { emerald: "text-emerald-400", rose: "text-rose-400", purple: "text-purple-400" };
  return (
    <div className="bg-slate-900/60 border border-slate-800/60 p-3.5 rounded-xl">
      <h4 className={`text-xs uppercase font-bold tracking-wider mb-1 ${colorMap[color]}`}>
        <i className={`fa-solid ${icon} mr-1.5`}/> {title}
      </h4>
      <p data-testid={testid} className="text-xs text-slate-300 leading-relaxed">{text || "—"}</p>
    </div>
  );
}
